/**
 * AgentStream — the Activity View (right, 380 px): case header and controls, the plan checklist,
 * then the stream (speech, tool-call chips, hand-offs, approval cards, comments, the summary card),
 * newest at the bottom with auto-scroll, and a typed input (Enter sends an intent).
 *
 * `stream` is the resolved list from selectStream ([{kind, id, item}]). Generic: specialists,
 * labels and permissions arrive as props.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Pause, Play, Square, Send, ArrowRight, MessageSquare, CircleCheck, Rewind, Hand,
} from 'lucide-react';
import PlanChecklist from './PlanChecklist.jsx';
import ToolCallChip from './ToolCallChip.jsx';
import SpeechLine from './SpeechLine.jsx';
import ApprovalCard from './ApprovalCard.jsx';
import SpecialistDot from './SpecialistDot.jsx';
import SyntheticBadge from './SyntheticBadge.jsx';
import { StatusChip } from './CaseRail.jsx';
import { canApprove } from './agentReducer.js';
import { clockOf, oneLine } from './agentUi.js';
import './AgentStream.css';

const STICK_PX = 48;

function HandoffLine({ handoff, specialists }) {
  const from = specialists[handoff.from];
  const to = specialists[handoff.to];
  return (
    <div className={`astream__handoff is-${handoff.status}`}>
      <SpecialistDot specialist={from} size={7} />
      <span className="astream__handoff-names">
        {from?.name ?? handoff.from} <ArrowRight size={11} aria-hidden="true" /> {to?.name ?? handoff.to}:
      </span>
      <span className="astream__handoff-task">{oneLine(handoff.task, 160)}</span>
      {handoff.status === 'done' && <CircleCheck size={12} className="astream__ok" aria-label="Done" />}
    </div>
  );
}

function CommentLine({ comment, specialists }) {
  const spec = comment.author?.kind === 'specialist' ? specialists[comment.author.id] : null;
  return (
    <div className="astream__comment">
      <MessageSquare size={11} aria-hidden="true" className="astream__comment-icon" />
      {spec ? <SpecialistDot specialist={spec} showName size={6} /> : <span className="astream__comment-author">{comment.author?.name ?? 'You'}</span>}
      <span className="astream__comment-body">{comment.body}</span>
      <span className="astream__speech-time">{clockOf(comment.created_at)}</span>
    </div>
  );
}

function SummaryCard({ caseObj }) {
  const s = caseObj?.summary;
  if (!s) return null;
  const refs = Object.values(s.refs || {}).filter(Boolean);
  return (
    <section className="astream__summary" aria-label="Case summary">
      <div className="astream__summary-head"><CircleCheck size={14} aria-hidden="true" /> Case closed</div>
      <div className="astream__summary-headline">{s.headline}</div>
      {s.outcome && <p className="astream__summary-outcome">{s.outcome}</p>}
      {refs.length > 0 && <div className="astream__summary-refs">{refs.join(' · ')}</div>}
      <SyntheticBadge />
    </section>
  );
}

export default function AgentStream({
  caseObj = null,
  plan = null,
  stream = [],
  drafts = [],
  specialists = {},
  principal = null,
  flashStep = null,
  voiceConfirm = null,
  roleLabel = (r) => r,
  assetLabel = (id) => id,
  onIntent,
  onControl,
  onOpenCitation,
  onDecide,
  onOpenOutput,
  onOpenFile,
  onApprovalEdit,
  onApprovalVisible,
  header = null,
  readOnly = false,
  narration = 'on',
  /** Passed to ApprovalCard for amounts without a currency (vertical-specific, from Dashboard). */
  defaultCurrency = null,
}) {
  const [expanded, setExpanded] = useState(() => new Set());
  const [text, setText] = useState('');
  const [confirmClose, setConfirmClose] = useState(false);
  const listRef = useRef(null);
  const stickRef = useRef(true);

  const toggle = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // Newest at the bottom: follow new items unless the user scrolled up to read.
  const count = stream.length;
  const lastKey = count ? `${stream[count - 1].kind}:${stream[count - 1].id}:${stream[count - 1].item?.status ?? ''}` : '';
  useEffect(() => {
    const el = listRef.current;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [count, lastKey]);

  useEffect(() => { setConfirmClose(false); }, [caseObj?.id]);

  const onScroll = () => {
    const el = listRef.current;
    if (el) stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_PX;
  };

  const submit = (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || typeof onIntent !== 'function') return;
    onIntent(t);
    setText('');
    stickRef.current = true;
  };

  const status = caseObj?.status;
  const closed = status === 'done' || status === 'cancelled' || status === 'failed';
  const draftById = Object.fromEntries((drafts || []).map((d) => [d.id, d]));

  return (
    <aside className="astream" aria-label="Agent activity">
      {header}
      {caseObj ? (
        <div className="astream__case">
          <div className="astream__case-row">
            <span className="astream__case-ref">{caseObj.ref}</span>
            <StatusChip status={status} />
            {caseObj.working === false && !closed && <span className="astream__chip-tag" title="Tool calls are held">Holding</span>}
            {narration === 'off' && <span className="astream__chip-tag" title="Narration is off">Quiet</span>}
          </div>
          <div className="astream__case-title">{caseObj.title}</div>
          <div className="astream__case-sub">
            {assetLabel(caseObj.asset)}{caseObj.unit ? ` · ${caseObj.unit}` : ''}{caseObj.site?.label ? ` · ${caseObj.site.label}` : ''}
          </div>
          <div className="astream__team">
            {(caseObj.team || [caseObj.specialist]).map((id) => (
              <SpecialistDot key={id} specialist={specialists[id] ?? { id }} showName size={7} />
            ))}
          </div>
          {!readOnly && !closed && typeof onControl === 'function' && (
            <div className="astream__controls">
              {status === 'paused' ? (
                <button type="button" className="astream__btn" onClick={() => onControl('resume')}><Play size={12} aria-hidden="true" /> Carry on</button>
              ) : (
                <button type="button" className="astream__btn" onClick={() => onControl('pause')}><Pause size={12} aria-hidden="true" /> Pause</button>
              )}
              {caseObj.working === false ? (
                <button type="button" className="astream__btn" onClick={() => onControl('start_working')}><Play size={12} aria-hidden="true" /> Start working</button>
              ) : (
                <button type="button" className="astream__btn" onClick={() => onControl('stop_working')} title="Hold tool calls"><Hand size={12} aria-hidden="true" /> Stop working</button>
              )}
              <button type="button" className="astream__btn" onClick={() => onControl('use_recorded')} title="Use the recorded result for this step (Shift+F)">
                <Rewind size={12} aria-hidden="true" /> Use recorded
              </button>
              {confirmClose ? (
                <>
                  <button type="button" className="astream__btn is-warn" onClick={() => { onControl('close'); setConfirmClose(false); }}>Close the case</button>
                  <button type="button" className="astream__btn" onClick={() => setConfirmClose(false)}>Keep open</button>
                </>
              ) : (
                <button type="button" className="astream__btn" onClick={() => setConfirmClose(true)}><Square size={12} aria-hidden="true" /> Close</button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="astream__case astream__empty">No case selected. Pick one on the left, or start the line replay.</div>
      )}

      <div className="astream__scroll" ref={listRef} onScroll={onScroll}>
        {caseObj && (
          <PlanChecklist
            steps={plan?.steps ?? []}
            flashStep={flashStep}
            specialists={specialists}
            readOnly={readOnly || closed}
            onGoto={onControl ? (index) => onControl('goto', { step_index: index }) : undefined}
            onSkip={onControl ? (stepId) => onControl('skip', { step_id: stepId }) : undefined}
          />
        )}

        <div className="astream__items" aria-live="polite">
          {stream.map(({ kind, id, item }) => {
            if (kind === 'speech') {
              return <SpeechLine key={`s-${id}`} speech={item} specialist={specialists[item.specialist]} onOpenCitation={onOpenCitation} onOpenFile={onOpenFile} />;
            }
            if (kind === 'call') {
              return (
                <ToolCallChip
                  key={`c-${id}`}
                  call={item}
                  specialist={specialists[item.specialist]}
                  expanded={expanded.has(id)}
                  onToggle={toggle}
                  onOpenCitation={onOpenCitation}
                />
              );
            }
            if (kind === 'handoff') return <HandoffLine key={`h-${id}`} handoff={item} specialists={specialists} />;
            if (kind === 'comment') return <CommentLine key={`m-${id}`} comment={item} specialists={specialists} />;
            if (kind === 'summary') return <SummaryCard key={`x-${id}`} caseObj={item} />;
            if (kind === 'approval') {
              const draft = draftById[item.draft_id] ?? null;
              return (
                <ApprovalCard
                  key={`a-${id}`}
                  approval={item}
                  draft={draft}
                  canApprove={canApprove(principal, item)}
                  voiceConfirm={voiceConfirm}
                  roleLabel={roleLabel}
                  readOnly={readOnly}
                  defaultCurrency={defaultCurrency}
                  onApprove={(draftVersion) => onDecide?.(item.id, 'approve', { draftVersion })}
                  onReject={(reason) => onDecide?.(item.id, 'reject', { reason })}
                  onEdit={() => onApprovalEdit?.(item)}
                  onVisible={onApprovalVisible}
                  onOpenFile={onOpenFile ?? onOpenOutput}
                  onOpenCitation={onOpenCitation}
                />
              );
            }
            return null;
          })}
        </div>
      </div>

      {!readOnly && (
        <form className="astream__input" onSubmit={submit}>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={caseObj ? 'Ask or instruct the specialist…' : 'Ask the agent…'}
            aria-label="Message the agent"
            maxLength={1000}
          />
          <button type="submit" className="astream__send" disabled={!text.trim()} aria-label="Send">
            <Send size={14} aria-hidden="true" />
          </button>
        </form>
      )}
    </aside>
  );
}
